import { useState } from 'react';
import UploadForm from './components/UploadForm';
import JobStatus from './components/JobStatus';
import Loading from './components/Loading';
import { useJobPolling } from './hooks/useJobPolling';
import { createJob, uploadFile, downloadPDF } from './services/api';
import { JobSubmission } from './types';
import { getFileMimeType } from './utils/fileValidation';
import innvoLogo from './assets/innvo_logo_transp.png';

function App() {
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [selectedFormat, setSelectedFormat] = useState<'pdf' | 'docx'>('docx');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const { jobStatus, error: pollingError } = useJobPolling({
    jobId: currentJobId,
  });

  const handleSubmit = async (data: JobSubmission) => {
    // Set submitting state immediately for instant UI feedback
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // Step 1: Create job
      const response = await createJob(
        data.candidateName,
        data.objective,
        data.file.name,
        getFileMimeType(data.file),
        data.outputFormat
      );

      // Step 2: Upload file to pre-signed URL
      await uploadFile(response.uploadUrl, data.file);

      // Step 3: Store selected format and start polling for status
      setSelectedFormat(data.outputFormat);
      setCurrentJobId(response.jobId);
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Erro ao enviar currículo. Tente novamente.';
      setSubmitError(errorMessage);
      console.error('Error submitting job:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDownload = async () => {
    if (!jobStatus) return;
    
    const downloadUrl = selectedFormat === 'pdf' 
      ? jobStatus.outputDownloadUrl 
      : jobStatus.outputDownloadUrlDocx;
    
    if (!downloadUrl) return;

    setIsDownloading(true);

    try {
      const extension = selectedFormat === 'pdf' ? 'pdf' : 'docx';
      const fileName = `CV_Innvo_${jobStatus.jobId}.${extension}`;
      await downloadPDF(downloadUrl, fileName);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Erro ao baixar o arquivo. Tente novamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleNewConversion = () => {
    setCurrentJobId(null);
    setSubmitError(null);
  };

  return (
    <div className="app">
      <header className="app-header">
        <img src={innvoLogo} alt="Innvo Logo" className="app-logo" />
        <h1 className="app-title">Innvo CV Converter</h1>
        <p className="app-subtitle">
          Sistema de conversão automática de currículos para o padrão Innvo
        </p>
      </header>

      {!currentJobId ? (
        <>
          <UploadForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
          {submitError && (
            <div className="error-details" style={{ marginTop: '20px' }}>
              <h4>Erro ao enviar:</h4>
              <p>{submitError}</p>
            </div>
          )}
        </>
      ) : (
        <>
          {jobStatus ? (
            <JobStatus
              jobStatus={jobStatus}
              onDownload={handleDownload}
              onNewConversion={handleNewConversion}
              isDownloading={isDownloading}
            />
          ) : (
            <Loading message="Carregando status do job..." size="large" />
          )}
          {pollingError && (
            <div className="error-details" style={{ marginTop: '20px' }}>
              <h4>Erro ao consultar status:</h4>
              <p>{pollingError}</p>
            </div>
          )}
        </>
      )}
      <footer className="app-footer">
        <p>v0.0.2</p>
      </footer>
    </div>
  );
}

export default App;
